import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNewerVersion } from '@repo/utils';

type VersionResponse = {
  current_version: string;
};

const INITIAL_CHECK_DELAY_MS = 100;
const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

async function clearCachesAndReload() {
  try {
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Cache Storage può rifiutare (es. private browsing): il reload basta comunque
  }
  window.location.reload();
}

export function AppVersionWatcher() {
  const { t } = useTranslation();
  const [showNotice, setShowNotice] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const scheduleCheck = useCallback((delayMs: number) => {
    if (import.meta.env.DEV) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      const localVersion = __APP_VERSION__;
      if (!localVersion) return;

      try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) {
          scheduleCheck(POLL_INTERVAL_MS);
          return;
        }

        const payload = (await response.json()) as VersionResponse;
        if (
          payload.current_version &&
          isNewerVersion(payload.current_version, localVersion)
        ) {
          setShowNotice(true);
          return; // Polling suspended while modal is visible
        }
      } catch {
        // Ignore transient network errors
      }

      scheduleCheck(POLL_INTERVAL_MS);
    }, delayMs);
  }, []);

  useEffect(() => {
    scheduleCheck(INITIAL_CHECK_DELAY_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [scheduleCheck]);

  if (!showNotice) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-1000 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="false"
        aria-label={t('versionNotice.title')}
        className="pointer-events-auto w-full max-w-md rounded-lg border-2 border-primary bg-background p-5 shadow-xl"
      >
        <h3 className="text-base font-semibold text-foreground">
          {t('versionNotice.title')}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('versionNotice.message')}
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowNotice(false);
              scheduleCheck(POLL_INTERVAL_MS);
            }}
            className="cursor-pointer rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
          >
            {t('versionNotice.later')}
          </button>
          <button
            type="button"
            onClick={() => void clearCachesAndReload()}
            className="cursor-pointer rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t('versionNotice.updateNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
